// A real league's own time zone, read once per league and shared.
//
// WHY ONE READ. Every time on the pool, the card and the move sheets is told
// in the league's zone (B-127), and the only place a real league says it is
// its clock (`GET /api/leagues/:id/periods`, `ClockView.timeZone`). Each of
// those read it for itself until the review of W8, and a read that failed was
// never asked again and never said so: one dropped request on a flaky
// connection left every move sheet on "Reading the move…" for good, the card
// on "Reading the league…", and the pool's rows without their clear times.
//
// SO: one entry per league, shared by every caller. A failure is an answer —
// the caller shows its sentence and a Try again — and it is also asked again
// by itself, a little later each time, while anybody is still looking. A zone
// that arrived is kept for the page's life: a league does not change zones
// under a person reading it, and a new page load reads it afresh.
//
// AND ON THE DEVICE (B-557, `kept.ts`): a page opened with no connection
// tells its times in the zone the league had when it was last read, where
// every sheet would otherwise wait on a clock it cannot reach. A league's
// zone does not move under a person, so a kept one serves the page as one
// that arrived does, and the next page load reads it afresh.

import { useEffect, useSyncExternalStore } from 'react';
import { ClockView } from '@fsp/domain';
import { get, messageOf } from './api';
import { GONE, NOT_NOW } from './kept';
import { onDevice } from './keptIdb';

type Entry =
  { state: 'loading' } | { state: 'ready'; zone: string } | { state: 'failed'; message: string };

const entries = new Map<string, Entry>();
const asking = new Set<string>();
/** How many failures in a row, for the wait before the next try. */
const misses = new Map<string, number>();
const listeners = new Set<() => void>();

function publish(leagueId: string, entry: Entry): void {
  entries.set(leagueId, entry);
  for (const listen of listeners) listen();
}

/** Ask for the league's clock, unless somebody already is. */
function ask(leagueId: string): void {
  if (asking.has(leagueId)) return;
  asking.add(leagueId);
  if (entries.get(leagueId)?.state !== 'ready') publish(leagueId, { state: 'loading' });
  const account = onDevice.account();
  const name = `zone:${leagueId}`;
  void get(`/leagues/${leagueId}/periods`, ClockView).then(async (reply) => {
    if (reply.status === 'ok') {
      asking.delete(leagueId);
      misses.delete(leagueId);
      onDevice.keep(account, name, { data: reply.data.timeZone, asOf: '' });
      publish(leagueId, { state: 'ready', zone: reply.data.timeZone });
      return;
    }
    if (reply.status === 'refused' && GONE.has(reply.code)) onDevice.drop(account, name);
    else if (
      (reply.status !== 'refused' || NOT_NOW.has(reply.code)) &&
      entries.get(leagueId)?.state !== 'ready'
    ) {
      const copy = await onDevice.find(account, name);
      if (copy !== null && typeof copy.data === 'string' && copy.data !== '') {
        asking.delete(leagueId);
        publish(leagueId, { state: 'ready', zone: copy.data });
        return;
      }
    }
    asking.delete(leagueId);
    const n = (misses.get(leagueId) ?? 0) + 1;
    misses.set(leagueId, n);
    publish(leagueId, {
      state: 'failed',
      message: messageOf(reply, 'The league’s clock could not be read.'),
    });
    // AGAIN, BY ITSELF, while somebody is looking: 2 s, 4 s, 8 s … 30 s.
    setTimeout(
      () => {
        if (listeners.size > 0 && entries.get(leagueId)?.state === 'failed') ask(leagueId);
      },
      Math.min(30_000, 1_000 * 2 ** n),
    );
  });
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen);
  return () => listeners.delete(listen);
}

export interface LeagueZoneRead {
  /** The league's IANA zone; null until it has been read. */
  zone: string | null;
  /** Why it could not be read, in words for the person; null while it can. */
  failure: string | null;
  /** Ask again now. */
  retry: () => void;
}

/** The league's zone, shared; `leagueId` null asks nothing (a practice league has its own). */
export function useLeagueZoneRead(leagueId: string | null): LeagueZoneRead {
  const entry = useSyncExternalStore(subscribe, () =>
    leagueId === null ? undefined : entries.get(leagueId),
  );
  // WHEN A READER ARRIVES, not whenever the entry changes: a failure seen by
  // an effect keyed on the entry would be asked again at once, forever. A
  // reader that finds a failure asks at once (the likeliest moment the
  // network is back); after that the backoff above and Try again do it.
  useEffect(() => {
    if (leagueId === null) return;
    const now = entries.get(leagueId);
    if (now === undefined || now.state === 'failed') ask(leagueId);
  }, [leagueId]);
  return {
    zone: entry?.state === 'ready' ? entry.zone : null,
    failure: entry?.state === 'failed' ? entry.message : null,
    retry: () => {
      if (leagueId !== null) ask(leagueId);
    },
  };
}
